import {
  FormControl,
  MenuItem,
  Select,
  Tab,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { Dispatch, SetStateAction } from 'react';
import { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';

import { Checkbox, FormControlLabel } from '@/contents/CampSite/styles';
import constContactCompany from '@/contents/constants/constContactCompany';
import constContactUser from '@/contents/constants/constContactUser';
import constMeta from '@/contents/constants/constMeta';
import ContactConfirmPage from '@/contents/Contact/ContactConfirmPage';
import Button from '@/contents/General/Button';
import Chip from '@/contents/General/Chip';
import Icon from '@/contents/General/Icon';
import { BaseContainer, Board } from '@/contents/General/Panels';
import Stack from '@/contents/General/Stack';
import Tabs from '@/contents/General/Tabs';
import TextField from '@/contents/General/TextField';
import Typography from '@/contents/General/Typography';
import { useBrowserBack } from '@/contents/hooks/browserBack';
import { GlobalContext } from '@/contents/hooks/state';
import Layout from '@/layouts';
import { useForm, yupEmail } from '@/modules/General/form';
import theme from '@/styles/theme';
import type { NextPageWithLayout } from '@/types';
import type { ContactFromInput } from '@/types/main';

const Form = ({
  tabValue,
  selectedCategory,
  setSelectedCategory,
}: {
  tabValue: string;
  selectedCategory: string;
  setSelectedCategory: Dispatch<SetStateAction<string>>;
}) => {
  const router = useRouter();
  const { setIsEdit, setIsEditLoading } = useContext(GlobalContext);
  const browerBack = useBrowserBack();
  const [confirm, setConfirm] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [page, setPage] = useState<'input' | 'confirm'>('input');
  const isPC = useMediaQuery('(min-width:900px)');

  const contactForm = yup.object({
    email: yupEmail,
    category: yup.string().required('タイトル・カテゴリを選択してください'),
    name: yup.string().required('お名前を入力してください'),
    content: yup.string().required('お問い合わせ内容を入力してください'),
  });
  const { register, watch, handleSubmit, control, errors, setValue } =
    useForm<ContactFromInput>(contactForm);

  const onChangeInputFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAttachedFile(file);
      setValue('file', [file]);
    }
  };

  const submit = () => {
    setIsEditLoading(true);
    const data = watch();
    const formData = new FormData();
    // eslint-disable-next-line no-restricted-syntax
    for (const key in data) {
      if (key === 'file') {
        // @ts-ignore
        formData.append(key, data[key][0]);
      } else {
        // @ts-ignore
        formData.append(key, data[key]);
      }
    }
    const response = fetch(
      `https://hyperform.jp/api/async/${
        tabValue === 'user' ? 'rccwcU8O' : '4IcAKLd8'
      }/complete`,
      {
        method: 'POST',
        body: formData,
      }
    ).then(() => {
      router.push(`/contact/complete?tabValue=${tabValue}`);
      setTimeout(() => setIsEditLoading(false), 2000);
    });
  };

  useEffect(() => {
    setIsEdit(true);
  }, []);

  useEffect(() => {
    if (process.browser) {
      window.scrollTo({
        top: 0,
      });
    }
  }, [page, errors]);

  if (page === 'confirm') {
    return (
      <>
        <ContactConfirmPage
          input={watch()}
          onSubmit={() => {
            submit();
          }}
          setPage={setPage}
        />
      </>
    );
  }

  return (
    <>
      <BaseContainer is_pc={isPC ? isPC.toString() : ''}>
        <Box paddingTop={5} paddingBottom={6}>
          <Typography fontSize={28} fontWeight={700}>
            お問い合わせフォーム
          </Typography>
        </Box>
        <Stack column space={4} paddingBottom={7}>
          <Box>
            <Stack between paddingBottom={2}>
              <Stack alignCenter space={1}>
                <Typography fontSize={16} fontWeight={700}>
                  タイトル・カテゴリ
                </Typography>
                <Chip label="必須" chipColor="required" chipSize={22} />
              </Stack>
            </Stack>
            <FormControl fullWidth>
              <Select
                error={!!errors.category}
                displayEmpty
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value as string);
                  setValue('category', e.target.value);
                }}
              >
                <MenuItem disabled value="" sx={{ display: 'none' }}>
                  <Typography fontSize={15} color="#C2C8C4">
                    選択してください
                  </Typography>
                </MenuItem>
                {(tabValue === 'user'
                  ? constContactUser
                  : constContactCompany
                ).map((item, index) => (
                  <MenuItem
                    value={item.label}
                    key={item.label + index}
                    sx={{ p: 2 }}
                  >
                    <Typography fontSize={15}>{item.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
              {!!errors.category && (
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color="#9B4450"
                  paddingTop={1}
                >
                  タイトル・カテゴリを選択してください
                </Typography>
              )}
            </FormControl>
          </Box>
          <Box>
            <Stack between paddingBottom={2}>
              <Stack alignCenter space={1}>
                <Typography fontSize={16} fontWeight={700}>
                  メールアドレス
                </Typography>
                <Chip label="必須" chipColor="required" chipSize={22} />
              </Stack>
              <Typography fontSize={14} fontWeight={500} color="grey">
                ※半角英数字・記号のみ
              </Typography>
            </Stack>
            <TextField
              placeholder="iihi@iihi.com"
              name="email"
              control={control}
            />
          </Box>
          {tabValue === 'company' && (
            <Box>
              <Stack between paddingBottom={2}>
                <Stack alignCenter space={1}>
                  <Typography fontSize={16} fontWeight={700}>
                    会社・団体名
                  </Typography>
                </Stack>
              </Stack>
              <TextField placeholder="iihi" name="company" control={control} />
            </Box>
          )}
          <Box>
            <Stack between paddingBottom={2}>
              <Stack alignCenter space={1}>
                <Typography fontSize={16} fontWeight={700}>
                  お名前
                </Typography>
                <Chip label="必須" chipColor="required" chipSize={22} />
              </Stack>
            </Stack>
            <TextField placeholder="山田 太郎" name="name" control={control} />
          </Box>
          <Box>
            <Stack between paddingBottom={2}>
              <Stack alignCenter space={1}>
                <Typography fontSize={16} fontWeight={700}>
                  内容を入力する
                </Typography>
                <Chip label="必須" chipColor="required" chipSize={22} />
              </Stack>
            </Stack>
            <TextField
              multiline
              rows={12}
              placeholder="お問い合わせ内容の詳細をご入力ください。"
              name="content"
              control={control}
            />
          </Box>
          <Box>
            <Stack between paddingBottom={2}>
              <Stack alignCenter space={1}>
                <Typography fontSize={16} fontWeight={700}>
                  添付ファイル
                </Typography>
                <Chip label="任意" chipColor="grey" chipSize={22} />
              </Stack>
            </Stack>
            <Button component="label" fullWidth>
              <Board sx={{ width: 'inherit' }}>
                <Stack column center>
                  <Icon
                    iconType="file"
                    size={56}
                    color={attachedFile ? 'primary' : ''}
                  />
                  <Box sx={{ '& p': { display: 'inline' } }}>
                    <Typography fontSize={15} color="primary">
                      {attachedFile ? attachedFile.name : 'ファイルを追加'}
                    </Typography>
                  </Box>
                </Stack>
              </Board>
              <input
                type="file"
                name="contactFile"
                accept="image/*,application/pdf"
                id="contact-file-input"
                hidden
                onChange={(e) => {
                  onChangeInputFile(e);
                }}
              />
            </Button>
          </Box>
        </Stack>
        <Stack column center space={4} paddingBottom={5}>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirm}
                onChange={() => setConfirm(!confirm)}
                icon={<Icon iconType="checkbox-off" />}
                checkedIcon={<Icon iconType="checkbox-on" />}
              />
            }
            label={
              <Typography color="primary">
                プライバシーポリシーに同意する
              </Typography>
            }
            sx={{
              alignItems: 'center !important',
              p: '11px 16px !important',
              width: 'fit-content !important',
              background: '#ffffff',
            }}
            className={confirm ? 'checked' : ''}
          />
          <Stack center>
            <Button
              onClick={handleSubmit((data) => {
                setPage('confirm');
              })}
              disabled={!confirm}
              buttonSize="md"
              sx={{ width: '289px' }}
            >
              入力内容を確認する
            </Button>
          </Stack>
        </Stack>
      </BaseContainer>
    </>
  );
};

const Index: NextPageWithLayout = () => {
  const router = useRouter();
  const path = router.query.path as 'user' | 'company';
  const { category } = router.query;
  const { isShowForm, setIsShowForm } = useContext(GlobalContext);
  const [tabValue, setTabValue] = useState<string>('user');
  const [consent, setConsent] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState('');
  const isPC = useMediaQuery('(min-width:900px)');

  useEffect(() => {
    setIsShowForm(false);
    setConsent(false);
  }, [router]);

  useEffect(() => {
    if (path) {
      setTabValue(path);
    }
  }, [path]);

  useEffect(() => {
    if (category) {
      setIsShowForm(true);
      setSelectedCategory(category as string);
    } else {
      setIsShowForm(false);
    }
  }, [category]);

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, [isShowForm]);

  const changeTab = (
    event: React.SyntheticEvent<Element, Event>,
    value: any
  ) => {
    setTabValue(value);
    router.replace(`/contact/${value}`);
  };

  return (
    <>
      <Head>
        <title>{`お問い合わせ《${
          path === 'user' ? 'ユーザ' : '企業/団体'
        }》 - iihi`}</title>
        <meta name="description" content={constMeta.contact.mata_description} />
        <meta
          property="og:title"
          content={`お問い合わせ《${
            path === 'user' ? 'ユーザ' : '企業/団体'
          }》 - iihi`}
        />
        <meta
          property="og:description"
          content={constMeta.contact.mata_description}
        />
        <meta
          property="og:image"
          content="https://iihi.life/assets/images/common/ogp.jpg"
        />
      </Head>
      <>
        {isShowForm ? (
          <Form
            tabValue={tabValue}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
          />
        ) : (
          <BaseContainer is_pc={isPC ? isPC.toString() : ''}>
            <Box paddingTop={5} paddingBottom={6}>
              <Typography fontSize={28} fontWeight={700} variant="h1">
                お問い合わせ
              </Typography>
            </Box>
            <Stack center paddingBottom={6}>
              <Tabs tabValue={tabValue} onChange={changeTab}>
                <Tab value="user" label="ユーザー" />
                <Tab value="company" label="企業・団体" />
              </Tabs>
            </Stack>
            {tabValue === 'user' && (
              <>
                <Box paddingBottom={5}>
                  <Typography fontSize={16} fontWeight={700} paddingBottom={2}>
                    ユーザーのみなさまへ
                  </Typography>
                  <Typography fontSize={15} lineHeight={1.8}>
                    いつもiihiをご利用いただきありがとうございます！
                    みなさんが使いやすく、キャンプがもっと楽しくなるサービスを目指して、キャンプ好きなメンバーで編成されたiihiチームは日々改善に取り組んでいます。
                    <br />
                    一方で、iihiチームはまだまだ少数で運営しているため、大変申し訳ありませんが
                    <span style={{ color: '#9B4450' }}>
                      すべてのお問い合わせに返信を行うことが叶いません。
                    </span>
                    複数のユーザーの方から同様のお問い合わせをいただいた場合は、
                    <Link href={'/news'}>
                      <a
                        style={{
                          color: '#2A607E',
                          textDecoration: 'underline',
                        }}
                      >
                        お知らせ
                      </a>
                    </Link>
                    ページにて通知いたします。
                    <br />
                    <br />
                    ご理解とご協力のほど、よろしくお願いいたします。
                  </Typography>
                </Box>
                <Board
                  sx={{ mb: theme.spacing(6), pt: theme.spacing(4) }}
                  no_border="true"
                >
                  <Typography
                    fontSize={16}
                    fontWeight={700}
                    color="red"
                    paddingBottom={2}
                  >
                    お問い合わせの前に まずはよくあるご質問をご覧ください！
                  </Typography>
                  <Typography fontSize={15} lineHeight={1.8}>
                    ユーザーのみなさまから届いたお問い合わせをよくあるご質問ページにまとめています。お問い合わせの前に、よくあるご質問のご確認をお願いいたします。
                  </Typography>
                  <Stack center paddingTop={4} paddingBottom={2}>
                    <Link href={`/faq`}>
                      <a>
                        <Button buttonSize="md" sx={{ width: '289px' }}>
                          よくあるご質問をみる
                        </Button>
                      </a>
                    </Link>
                  </Stack>
                </Board>
              </>
            )}
            {tabValue === 'company' && (
              <Box paddingBottom={5}>
                <Typography fontSize={16} fontWeight={700} paddingBottom={2}>
                  企業・団体のみなさま、取材をご希望の方へ
                </Typography>
                <Typography fontSize={15} lineHeight={1.8}>
                  この度は、iihiにご関心いただきありがとうございます！
                  <br />
                  私たちは、サービスを通じてアウトドア業界を盛り上げたいと考えております。
                  キャンプ場やギアの広告・プロモーションのコラボレーション企画、イベント出展のご依頼など、iihiの理念に共感いただける企業・団体さまからのご連絡をお待ちしております。
                  <br />
                  なお、iihiは現在少ない人数で運営しているため、ご連絡いただいた内容が一方的な営業・勧誘目的などの場合は、お返事をいたしかねますのであらかじめご了承ください。
                </Typography>
              </Box>
            )}
            <Stack column center space={4} paddingBottom={5}>
              <Box>
                <Typography fontSize={16} fontWeight={700} paddingBottom={2}>
                  お問い合わせフォーム
                </Typography>
                <Typography fontSize={15} lineHeight={1.8}>
                  {tabValue === 'user'
                    ? '「よくあるご質問を確認しました」に同意の上、お問い合わせください。'
                    : '「営業・勧誘目的ではありません」に同意の上、お問い合わせください。'}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consent}
                    onChange={() => setConsent(!consent)}
                    icon={<Icon iconType="checkbox-off" />}
                    checkedIcon={<Icon iconType="checkbox-on" />}
                  />
                }
                label={
                  <Typography color="primary">
                    {tabValue === 'user'
                      ? 'よくあるご質問を確認しました'
                      : '営業・勧誘目的ではありません'}
                  </Typography>
                }
                sx={{
                  alignItems: 'center !important',
                  p: '11px 16px !important',
                  width: 'fit-content !important',
                  background: '#ffffff',
                }}
                className={consent ? 'checked' : ''}
              />
              <Stack center>
                <Button
                  onClick={() => setIsShowForm(true)}
                  disabled={!consent}
                  buttonSize="md"
                  sx={{ width: '289px' }}
                >
                  お問い合わせはこちら
                </Button>
              </Stack>
            </Stack>
          </BaseContainer>
        )}
      </>
    </>
  );
};

Index.getLayout = (page) => <Layout>{page}</Layout>;

export default Index;
